import {lazy, Suspense} from 'react'
import {ThemeProvider} from '@primer/react-brand/esm'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isStaff} from '@github-ui/stats'

import {FootnotesProvider} from '../../../components/contentful/ContentfulFootnotes/FootnotesContext'
import type {FlexPage} from '../../../schemas/contentful/contentTypes/flexTemplate'
import {PersonalizationProvider, type PersonalizationContextValue} from '../../../contexts/PersonalizationContext'
import {useDomReady} from '../../../lib/utils/platform'
import {useNinetailedConfig} from '../../../contexts/NinetailedConfigContext'
import type {NinetailedPreviewEntries} from '../../../lib/utils/ninetailed-preview'

import {FlexTemplateContent} from './FlexTemplateContent'

// Lazy load Ninetailed wrapper to prevent SSR bundling
const NinetailedWrapper = lazy(() => import('../../../components/ninetailed/NinetailedWrapper'))
const NinetailedPreviewWrapper = lazy(() => import('../../../components/ninetailed/NinetailedPreviewWrapper'))

export type FlexTemplateProps = {
  page: FlexPage
  userLoggedIn?: boolean
  hasNtExperiences?: boolean
  ninetailedPreview?: NinetailedPreviewEntries
}

export function FlexTemplate({page, userLoggedIn, hasNtExperiences, ninetailedPreview}: FlexTemplateProps) {
  const isDomReady = useDomReady()
  const ninetailedConfig = useNinetailedConfig()

  const personalizationValue: PersonalizationContextValue = {
    userState: {
      isLoggedIn: userLoggedIn,
    },
  }

  const templateContent = (
    <ThemeProvider
      colorMode={page.fields.settings?.fields.colorMode ?? 'light'}
      style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
    >
      <PersonalizationProvider value={personalizationValue}>
        <FootnotesProvider>
          <FlexTemplateContent page={page} />
        </FootnotesProvider>
      </PersonalizationProvider>
    </ThemeProvider>
  )

  // Only wrap with Ninetailed when experiences exist and DOM is ready
  if (hasNtExperiences && ninetailedConfig && isDomReady) {
    // Use preview wrapper when flag is enabled AND user is a GitHub staff member.
    // This ensures the preview UI is only visible to internal employees.
    const usePreview = isFeatureEnabled('landing_pages_ninetailed_preview') && isStaff() && ninetailedPreview != null

    if (usePreview) {
      return (
        <Suspense fallback={null}>
          <NinetailedPreviewWrapper
            clientId={ninetailedConfig.clientId}
            environment={ninetailedConfig.environment}
            locale={ninetailedConfig.locale}
            rawExperiences={ninetailedPreview.experiences}
            rawAudiences={ninetailedPreview.audiences}
          >
            {templateContent}
          </NinetailedPreviewWrapper>
        </Suspense>
      )
    }

    return (
      <Suspense fallback={null}>
        <NinetailedWrapper
          clientId={ninetailedConfig.clientId}
          environment={ninetailedConfig.environment}
          locale={ninetailedConfig.locale}
        >
          {templateContent}
        </NinetailedWrapper>
      </Suspense>
    )
  }

  return templateContent
}
